import { cx } from '@/utils/strings'
import { ReactNode } from 'react'
import backgroundStyles from '@/constants/background-styles'

export interface GradientBackgroundWrapperProps {
  readonly id?: string
  readonly children: ReactNode
  readonly backgroundStyle?: string
  readonly className?: string
  readonly innerClassName?: string
  readonly containerClassName?: string
  readonly unconstrainedBackground?: boolean
}

export default function GradientBackgroundWrapper({
  id,
  children,
  backgroundStyle,
  className = '',
  innerClassName = '',
  containerClassName = '',
  unconstrainedBackground,
  ...rest
}: GradientBackgroundWrapperProps) {
  // Determine if we should apply gradient border effect
  const acceptedBgs = backgroundStyle != null && backgroundStyle !== backgroundStyles.midnightGradient

  const shouldApplyGradientBorder = acceptedBgs === true

  // Check if outer div needs any classes
  const hasOuterDivClasses = shouldApplyGradientBorder || className.trim() !== ''

  // Check if inner div needs any classes
  const hasInnerDivClasses = acceptedBgs || innerClassName.trim() !== ''

  // If no wrapper divs are needed, just render children in section
  if (!hasOuterDivClasses && !hasInnerDivClasses) {
    return (
      <section
        id={id}
        className={cx(backgroundStyle !== backgroundStyles.midnightGradient && 'max-w-8xl mx-auto', containerClassName)}
        {...rest}
      >
        {children}
      </section>
    )
  }

  // If only inner div is needed
  if (!hasOuterDivClasses && hasInnerDivClasses) {
    return (
      <section
        id={id}
        className={cx(backgroundStyle !== backgroundStyles.midnightGradient && 'max-w-8xl mx-auto', containerClassName)}
        {...rest}
      >
        <div
          className={cx(
            backgroundStyle && 'relative overflow-hidden',
            backgroundStyle?.includes('Gradient With Curves') &&
              'rounded-lg relative z-3 before:content-[""] before:absolute before:bg-cover before:bg-center before:bg-no-repeat before:opacity-100 before:-z-1 before:top-0 before:left-0 before:right-0 before:bottom-0',
            backgroundStyle === backgroundStyles.gradientWithCurves1 && 'gradient-curve-1',
            backgroundStyle === backgroundStyles.gradientWithCurves2 && 'gradient-curve-2',
            backgroundStyle === backgroundStyles.gradientWithCurves3 && 'gradient-curve-3',
            backgroundStyle === backgroundStyles.gradientWithCurves1Portrait && 'gradient-curve-portrait-1',
            backgroundStyle === backgroundStyles.gradientWithCurves2Portrait && 'gradient-curve-portrait-2',
            backgroundStyle === backgroundStyles.gradientWithCurves1Square && 'gradient-curve-square-1',
            backgroundStyle === backgroundStyles.gradientWithCurves2Square && 'gradient-curve-square-2',
            backgroundStyle === backgroundStyles.gradientWithCurves3Square && 'gradient-curve-square-3',
            backgroundStyle === backgroundStyles.gradientWithCurves4Square && 'gradient-curve-square-4',
            backgroundStyle === backgroundStyles.gradientWithCurves5Square && 'gradient-curve-square-5',
            backgroundStyle === backgroundStyles.gradientWithCurves6Square && 'gradient-curve-square-6',
            backgroundStyle === backgroundStyles.gradientWithCurves7Square && 'gradient-curve-square-7',
            backgroundStyle === backgroundStyles.gradientWithCurves8Square && 'gradient-curve-square-8',
            (backgroundStyle === backgroundStyles.gradient1 || backgroundStyle === backgroundStyles.gradient2) &&
              'relative z-3',
            backgroundStyle === backgroundStyles.gradient1 && 'gradient-bg-1',
            backgroundStyle === backgroundStyles.gradient2 && 'gradient-bg-2',
            backgroundStyle === backgroundStyles.midnightGradient && 'bg-midnight-gradient',
            backgroundStyle === backgroundStyles.glacierGradient && 'bg-glacier-gradient',
            backgroundStyle === backgroundStyles.skyBlue && 'bg-bw-sky-blue dark:bg-secondary-900',
            backgroundStyle !== backgroundStyles.midnightGradient && !unconstrainedBackground && 'rounded-lg',
            innerClassName
          )}
        >
          {children}
        </div>
      </section>
    )
  }

  // Render both divs when needed
  return (
    <section
      id={id}
      className={cx(
        backgroundStyle !== backgroundStyles.midnightGradient && !unconstrainedBackground && 'max-w-8xl mx-auto',
        containerClassName
      )}
      {...rest}
    >
      <div
        className={cx(
          backgroundStyle !== backgroundStyles.midnightGradient &&
            !unconstrainedBackground &&
            'rounded-lg after:rounded-lg',
          shouldApplyGradientBorder &&
            !unconstrainedBackground &&
            'mx-5 lg:mx-9 bw-gradient-border-effect-1 relative p-[1px] after:z-1 after:content:[""] after:absolute after:top-[1px] after:left-[1px] after:right-[1px] after:bottom-[1px] after:bg-white dark:after:bg-secondary-900',

          className
        )}
      >
        {hasInnerDivClasses ? (
          <div
            className={cx(
              'overflow-hidden relative z-3',
              backgroundStyle?.includes('Gradient With Curves') &&
                'rounded-lg relative z-3 before:content-[""] before:absolute before:bg-cover before:bg-center before:bg-no-repeat before:opacity-50 before:-z-1 before:top-0 before:left-0 before:right-0 before:bottom-0',
              backgroundStyle === backgroundStyles.gradientWithCurves1 && 'gradient-curve-1',
              backgroundStyle === backgroundStyles.gradientWithCurves2 && 'gradient-curve-2',
              backgroundStyle === backgroundStyles.gradientWithCurves3 && 'gradient-curve-3',
              backgroundStyle === backgroundStyles.gradientWithCurves1Portrait && 'gradient-curve-portrait-1',
              backgroundStyle === backgroundStyles.gradientWithCurves2Portrait && 'gradient-curve-portrait-2',
              backgroundStyle === backgroundStyles.gradientWithCurves1Square && 'gradient-curve-square-1',
              backgroundStyle === backgroundStyles.gradientWithCurves2Square && 'gradient-curve-square-2',
              backgroundStyle === backgroundStyles.gradientWithCurves3Square && 'gradient-curve-square-3',
              backgroundStyle === backgroundStyles.gradientWithCurves4Square && 'gradient-curve-square-4',
              backgroundStyle === backgroundStyles.gradientWithCurves5Square && 'gradient-curve-square-5',
              backgroundStyle === backgroundStyles.gradientWithCurves6Square && 'gradient-curve-square-6',
              backgroundStyle === backgroundStyles.gradientWithCurves7Square && 'gradient-curve-square-7',
              backgroundStyle === backgroundStyles.gradientWithCurves8Square && 'gradient-curve-square-8',
              backgroundStyle === backgroundStyles.gradient1 && 'gradient-bg-1',
              backgroundStyle === backgroundStyles.gradient2 && 'gradient-bg-2',
              backgroundStyle === backgroundStyles.glacierGradient && 'bg-glacier-gradient',
              backgroundStyle === backgroundStyles.midnightGradient && 'bg-midnight-gradient',
              backgroundStyle === backgroundStyles.skyBlue && 'bg-bw-sky-blue dark:bg-secondary-900',
              backgroundStyle !== backgroundStyles.midnightGradient && !unconstrainedBackground && 'rounded-lg',
              innerClassName
            )}
          >
            {unconstrainedBackground ? <div className="max-w-8xl mx-auto px-5 lg:px-9">{children}</div> : children}
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  )
}
